// ============================================================
// BattlefieldBuildOverlay - what the next click will do.
//
// The hand cursor was imprecise because it was the CAMERA'S cursor: it
// said "you can drag the view", which is exactly what you are not doing
// while building. A crosshair helps, but what actually makes building
// precise is seeing the cell you are about to affect, and the footprint
// a drag has covered so far, before you commit to either.
// ============================================================
import { memo } from 'react';
import type { Camera } from '../engine/battlefield/projection';
import { project, SCENE_VIEW } from '../engine/battlefield/projection';
import type { Cell2 } from '../engine/battlefield/buildGesture';
import { outlineCells } from '../engine/battlefield/buildGesture';
import styles from '../styles/Battlefield.module.css';

/** A cell's four ground corners, projected, as an SVG points string. */
function cellPoints(cell: Cell2, size: number, camera: Camera, y = 0.03): string {
  return [
    { x: cell.x, z: cell.z },
    { x: cell.x + 1, z: cell.z },
    { x: cell.x + 1, z: cell.z + 1 },
    { x: cell.x, z: cell.z + 1 },
  ]
    .map((c) => project({ x: c.x * size, y, z: c.z * size }, camera, SCENE_VIEW))
    .map((p) => `${p.x.toFixed(1)},${p.y.toFixed(1)}`)
    .join(' ');
}

interface BattlefieldBuildOverlayProps {
  camera: Camera;
  size: number;
  /** The arena ring being drawn by hand, in ground metres. */
  arenaDraft?: { x: number; z: number }[] | null;
  /** The cell under the pointer. */
  hover?: Cell2 | null;
  /** The footprint the current drag covers, how deep, and which way it goes. */
  preview?: { cells: Cell2[]; erasing: boolean; level?: number; layers?: number } | null;
  /** The region Select has marked, which persists until it is replaced. */
  region?: { from: Cell2; to: Cell2 } | null;
  /** Where a held stamp would land, under the pointer. */
  ghost?: Cell2[] | null;
}

/**
 * Past this many cells the ghost draws only the footprint's OUTLINE.
 *
 * A preview of forty thousand `<polygon>` elements is what actually
 * freezes a browser mid-drag, and it is pure decoration - the build
 * itself is untouched, and greedy meshing turns the committed result
 * into a handful of boxes either way. The outline of a shape still looks
 * like that shape, which is all a ghost has to do.
 */
const PREVIEW_CELL_LIMIT = 2000;

/** The ghost's cells: all of them, or only the outline past the limit. */
function previewCells(preview: BattlefieldBuildOverlayProps['preview']) {
  if (!preview) return [];
  return preview.cells.length > PREVIEW_CELL_LIMIT ? outlineCells(preview.cells) : preview.cells;
}

/**
 * The arena being drawn: the ring so far, and a dot per corner so "click
 * the first corner to close" has something to aim at.
 */
function ArenaDraft({ draft, camera }: { draft: { x: number; z: number }[]; camera: Camera }) {
  const pts = draft.map((v) => project({ x: v.x, y: 0, z: v.z }, camera, SCENE_VIEW));
  if (!pts.length) return null;
  return (
    <g data-testid="arena-draft" data-corners={pts.length}>
      <polyline
        className={styles.arenaDraftLine}
        points={pts.map((p) => `${p.x.toFixed(1)},${p.y.toFixed(1)}`).join(' ')}
      />
      {pts.map((p, i) => (
        <circle key={i} className={styles.arenaDraftDot} cx={p.x} cy={p.y} r={i === 0 ? 5 : 3} />
      ))}
    </g>
  );
}

/** A set of cells as polygons, one class for all. */
function CellPolygons({
  cells,
  prefix,
  testId,
  className,
  size,
  camera,
}: {
  cells: Cell2[];
  prefix: string;
  testId: string;
  className: string;
  size: number;
  camera: Camera;
}) {
  return cells.map((cell) => (
    <polygon
      key={`${prefix}${cell.x},${cell.z}`}
      data-testid={testId}
      className={className}
      points={cellPoints(cell, size, camera)}
    />
  ));
}

/** Is there anything at all to draw? */
const isIdle = ({ hover, preview, region, ghost, arenaDraft }: BattlefieldBuildOverlayProps) =>
  ![hover, preview, region, ghost, arenaDraft].some(Boolean);

function BattlefieldBuildOverlayImpl(props: BattlefieldBuildOverlayProps) {
  const { camera, size, hover = null, preview = null, region = null, ghost = null } = props;
  if (isIdle(props)) return null;
  const cells = previewCells(preview);
  return (
    <g data-testid="build-overlay" className={styles.overlay} data-preview-cells={preview?.cells.length ?? 0}>
      <ArenaDraft draft={props.arenaDraft ?? []} camera={camera} />
      <CellPolygons
        cells={cells}
        prefix="p"
        testId="build-preview-cell"
        className={preview?.erasing ? styles.previewErase : styles.previewFill}
        size={size}
        camera={camera}
      />
      {/* THE MARQUEE. It was set and never drawn, so a selection appeared
          to vanish the instant the drag ended. It stays until it is
          replaced - that is what makes it a selection. */}
      {region && (
        <polygon
          data-testid="build-region"
          className={styles.regionMarquee}
          points={regionPoints(region.from, region.to, size, camera)}
        />
      )}
      <CellPolygons
        cells={ghost ?? []}
        prefix="g"
        testId="build-ghost-cell"
        className={styles.previewFill}
        size={size}
        camera={camera}
      />
      {/* HOW TALL, AND HOW HIGH. Building happens from the top-down view,
          where elevation moves nothing on screen by definition - so a
          number is the only honest cue there is. Markers have printed
          their height beside their name since v7.17.0; structures
          printed nothing at all. */}
      {preview && cells.length > 0 && <BuildLabel cells={cells} size={size} camera={camera} preview={preview} />}
      {hover && !preview && (
        <polygon data-testid="build-hover-cell" className={styles.hoverCell} points={cellPoints(hover, size, camera)} />
      )}
    </g>
  );
}

/**
 * The height ghost, ON ITS OWN BACKGROUND.
 *
 * Floating text with a dark halo is legible over most things and not
 * over all of them - and the one place it has to be read is directly
 * over whatever you are building, in whatever colour you chose. A pill
 * in the panel's own colours never touches the structure at all.
 *
 * The width is estimated from the string rather than measured: `getBBox`
 * means a second render for a label of a dozen characters, and the
 * estimate only has to be generous.
 */
const LABEL_CHAR_WIDTH = 6.6;
const LABEL_HEIGHT = 17;

interface BuildLabelProps {
  cells: Cell2[];
  size: number;
  camera: Camera;
  preview: { level?: number; layers?: number };
}

function BuildLabel({ cells, size, camera, preview }: BuildLabelProps) {
  const text = describeBuild(preview.layers, preview.level, size);
  const at = labelAt(cells, size, camera);
  const width = text.length * LABEL_CHAR_WIDTH + 12;

  return (
    <g data-testid="build-height" className={styles.buildLabel}>
      <rect
        x={at.x - width / 2}
        y={at.y - LABEL_HEIGHT + 4}
        width={width}
        height={LABEL_HEIGHT}
        rx={3}
        className={styles.buildLabelPill}
      />
      <text x={at.x} y={at.y} textAnchor="middle" className={styles.buildHeight}>
        {text}
      </text>
    </g>
  );
}

/** What the drag is about to build, in words. */
export function describeBuild(layers = 1, level = 0, size = 1): string {
  const tall = `${layers} cube${layers === 1 ? '' : 's'}`;
  if (!level) return tall;
  // METRES, not storeys, because a storey is not a picture and four
  // metres is. Signed the same way a marker's label is: a trench is as
  // worth knowing about as a gantry.
  const metres = level * size;
  const places = Math.abs(metres) % 1 ? 1 : 0;
  return `${tall} · ${metres > 0 ? '↑' : '↓'}${Math.abs(metres).toFixed(places)} m`;
}

/** Where to put the label: above the middle of the footprint. */
function labelAt(cells: Cell2[], size: number, camera: Camera) {
  const x = cells.reduce((sum, c) => sum + c.x, 0) / cells.length;
  const z = cells.reduce((sum, c) => sum + c.z, 0) / cells.length;
  const p = project({ x: x * size, y: 0.1, z: z * size }, camera, SCENE_VIEW);
  return { x: p.x, y: p.y - 10 };
}

/** The outline of the rectangle between two cells, on the ground. */
function regionPoints(from: Cell2, to: Cell2, size: number, camera: Camera): string {
  const x0 = (Math.min(from.x, to.x) - 0.5) * size;
  const x1 = (Math.max(from.x, to.x) + 0.5) * size;
  const z0 = (Math.min(from.z, to.z) - 0.5) * size;
  const z1 = (Math.max(from.z, to.z) + 0.5) * size;
  return [
    { x: x0, z: z0 },
    { x: x1, z: z0 },
    { x: x1, z: z1 },
    { x: x0, z: z1 },
  ]
    .map(({ x, z }) => project({ x, y: 0.06, z }, camera, SCENE_VIEW))
    .map((p) => `${p.x.toFixed(1)},${p.y.toFixed(1)}`)
    .join(' ');
}

export default memo(BattlefieldBuildOverlayImpl);
